"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./queryKeys";
import type { AdminUser } from "./types";
import {
  clearTokens,
  getRefreshToken,
  hasAccessToken,
  setStoredUser,
  setTokens,
} from "./tokens";

interface LoginResponse {
  access: string;
  refresh: string;
  user: AdminUser;
}

export async function login(username: string, password: string): Promise<AdminUser> {
  const data = await api.post<LoginResponse>(
    "/auth/login/",
    { username, password },
    { skipAuth: true }
  );
  setTokens(data.access, data.refresh);
  setStoredUser({
    id: data.user.id,
    username: data.user.username,
    email: data.user.email,
    full_name: data.user.full_name,
    is_staff: data.user.is_staff,
    is_superuser: data.user.is_superuser,
  });
  return data.user;
}

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.get<AdminUser>("/auth/me/"),
    // Without a token this can only answer 401, and asking would trigger the
    // client's redirect-to-login on a page that may already be /login.
    enabled: hasAccessToken(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const refresh = getRefreshToken();
      if (refresh) {
        try {
          await api.post("/auth/logout/", { refresh });
        } catch {
          // Best effort: the token may already be expired or blacklisted. The
          // local session is cleared either way — refusing to sign someone out
          // because the server could not be told is the wrong failure mode.
        }
      }
    },
    onSettled: () => {
      clearTokens();
      qc.clear();
      if (typeof window !== "undefined") window.location.href = "/login";
    },
  });
}
